
import React, { useMemo, useState } from "react";

import { useGetMonthlyRevenueQuery } from "../../slices/billingDashboardApiSlice";

const MonthlyRevenueScreen = () => {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);

  const { data, isLoading, error, isFetching } = useGetMonthlyRevenueQuery({
    year,
  });

  const revenue = data?.data;

  // =====================================================
  // Helpers
  // =====================================================

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // =====================================================
  // Monthly Revenue
  // =====================================================

  const monthlyRevenue = revenue?.monthlyRevenue || [];

const totalRevenue = monthlyRevenue.reduce(
  (sum, item) => sum + Number(item.revenue || 0),
  0,
);

const totalTaxableRevenue = monthlyRevenue.reduce(
  (sum, item) => sum + Number(item.taxableRevenue || 0),
  0,
);

const totalTaxCollected = monthlyRevenue.reduce(
  (sum, item) => sum + Number(item.taxCollected || 0),
  0,
);

const totalInvoices = monthlyRevenue.reduce(
  (sum, item) => sum + Number(item.invoiceCount || 0),
  0,
);

  const averageRevenuePerInvoice =
    totalInvoices > 0 ? totalRevenue / totalInvoices : 0;

  const averageTaxablePerInvoice =
    totalInvoices > 0 ? totalTaxableRevenue / totalInvoices : 0;

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div
          className="
            animate-spin
            h-10
            w-10
            border-4
            border-blue-600
            border-t-transparent
            rounded-full
          "
        />
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (error) {
    return (
      <div
        className="
          bg-red-50
          border
          border-red-200
          text-red-700
          rounded-lg
          p-5
        "
      >
        {error?.data?.message || error?.error || "Unable to load revenue"}
      </div>
    );
  }

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="space-y-6">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        className="
          flex
          flex-col
          md:flex-row
          justify-between
          items-start
          md:items-center
          gap-4
        "
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Monthly Revenue</h1>

          <p className="text-gray-500 mt-1">
            Revenue generated from paid invoices
          </p>
        </div>

        <div className="flex items-center gap-3">
          {isFetching && (
            <span className="text-xs text-blue-600">Updating...</span>
          )}

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="
              w-44
              border
              border-gray-300
              rounded-lg
              px-3
              py-2
              bg-white
              focus:outline-none
              focus:ring-2
              focus:ring-blue-500
            "
          >
            {Array.from({ length: 6 }, (_, i) => currentYear - i).map((yr) => (
              <option key={yr} value={yr}>
                {yr}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* =====================================================
          YEAR SUMMARY
      ===================================================== */}

      <div
        className="
          grid
          grid-cols-1
          sm:grid-cols-2
          lg:grid-cols-4
          gap-5
        "
      >
        {/* Total Revenue */}

        <SummaryCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue)}
          subtitle="GST inclusive"
          color="bg-green-600"
        />

        {/* Taxable Revenue */}

        <SummaryCard
          title="Taxable Revenue"
          value={formatCurrency(totalTaxableRevenue)}
          subtitle="Before GST"
          color="bg-blue-600"
        />

        {/* GST */}

        <SummaryCard
          title="GST Collected"
          value={formatCurrency(totalTaxCollected)}
          subtitle="Total GST"
          color="bg-purple-600"
        />

        {/* Paid Invoices */}

        <SummaryCard
          title="Paid Invoices"
          value={totalInvoices}
          subtitle={`During ${revenue?.year || year}`}
          color="bg-cyan-600"
        />
      </div>

      {/* =====================================================
          AVERAGES
      ===================================================== */}

      <div
        className="
          grid
          grid-cols-1
          md:grid-cols-2
          gap-5
        "
      >
        <div className="bg-white shadow rounded-xl p-5 border border-gray-100">
          <p className="text-sm text-gray-500">Average Revenue / Invoice</p>

          <h3 className="text-2xl font-bold text-gray-800 mt-2">
            {formatCurrency(averageRevenuePerInvoice)}
          </h3>

          <p className="text-xs text-gray-400 mt-1">GST inclusive</p>
        </div>

        <div className="bg-white shadow rounded-xl p-5 border border-gray-100">
          <p className="text-sm text-gray-500">Average Taxable / Invoice</p>

          <h3 className="text-2xl font-bold text-gray-800 mt-2">
            {formatCurrency(averageTaxablePerInvoice)}
          </h3>

          <p className="text-xs text-gray-400 mt-1">Before GST</p>
        </div>
      </div>

      {/* =====================================================
          MONTHLY CARDS
      ===================================================== */}

      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Monthly Breakdown
          </h3>

          <p className="text-sm text-gray-500">
            Revenue details for {revenue?.year || year}
          </p>
        </div>

        <div
          className="
            grid
            grid-cols-1
            sm:grid-cols-2
            lg:grid-cols-3
            xl:grid-cols-4
            gap-5
          "
        >
          {monthlyRevenue.map((month) => (
            <div
              key={month.month}
              className="
                bg-white
                shadow
                rounded-xl
                p-5
                border
                border-gray-100
              "
            >
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-800">
                  {month.monthName}
                </h3>

                <span className="text-xs text-gray-400">{month.month}</span>
              </div>

              {/* Revenue */}

              <div className="mt-4">
                <p className="text-xs text-gray-500">Revenue</p>

                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(month.revenue)}
                </p>
              </div>

              {/* Taxable */}

              <div className="mt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Taxable</span>

                  <span className="font-medium text-blue-600">
                    {formatCurrency(month.taxableRevenue)}
                  </span>
                </div>
              </div>

              {/* GST */}

              <div className="mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">GST</span>

                  <span className="font-medium text-purple-600">
                    {formatCurrency(month.taxCollected)}
                  </span>
                </div>
              </div>

              {/* Invoices */}

              <div
                className="
                  border-t
                  mt-4
                  pt-3
                  flex
                  justify-between
                  text-sm
                "
              >
                <span className="text-gray-500">Paid Invoices</span>

                <span className="font-semibold text-gray-800">
                  {month.invoiceCount || 0}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* =====================================================
          REVENUE TABLE
      ===================================================== */}

      <div className="bg-white shadow rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="font-semibold text-lg">Monthly Revenue Details</h3>

          <p className="text-sm text-gray-500 mt-1">
            Paid invoice revenue for {revenue?.year || year}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-4">Month</th>

                <th className="text-right p-4">Taxable Revenue</th>

                <th className="text-right p-4">GST</th>

                <th className="text-right p-4">Total Revenue</th>

                <th className="text-right p-4">Paid Invoices</th>
              </tr>
            </thead>

            <tbody>
              {!monthlyRevenue.length ? (
                <tr>
                  <td
                    colSpan="5"
                    className="
                      p-10
                      text-center
                      text-gray-500
                    "
                  >
                    No revenue data found for {revenue?.year || year}.
                  </td>
                </tr>
              ) : (
                monthlyRevenue.map((month) => (
                  <tr
                    key={month.month}
                    className="
                      border-t
                      hover:bg-gray-50
                    "
                  >
                    <td className="p-4 font-medium">{month.monthName}</td>

                    <td className="p-4 text-right text-blue-600">
                      {formatCurrency(month.taxableRevenue)}
                    </td>

                    <td className="p-4 text-right text-purple-600">
                      {formatCurrency(month.taxCollected)}
                    </td>

                    <td className="p-4 text-right font-semibold text-green-600">
                      {formatCurrency(month.revenue)}
                    </td>

                    <td className="p-4 text-right">
                      {month.invoiceCount || 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr className="bg-blue-50 font-semibold">
                <td className="p-4">Total</td>

                <td className="p-4 text-right text-blue-700">
                  {formatCurrency(totalTaxableRevenue)}
                </td>

                <td className="p-4 text-right text-purple-700">
                  {formatCurrency(totalTaxCollected)}
                </td>

                <td className="p-4 text-right text-green-700">
                  {formatCurrency(totalRevenue)}
                </td>

                <td className="p-4 text-right">{totalInvoices}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* =====================================================
          REVENUE EXPLANATION
      ===================================================== */}

      <div
        className="
          bg-blue-50
          border
          border-blue-100
          rounded-lg
          p-4
          text-sm
          text-blue-700
        "
      >
        <p className="font-semibold">Revenue calculation</p>

        <p className="mt-1">
          Total revenue is the GST-inclusive amount from invoices marked as
          paid. Taxable revenue represents the amount before GST, while GST
          collected represents the tax component of those paid invoices.
        </p>
      </div>
    </div>
  );
};

// =====================================================
// Summary Card
// =====================================================

const SummaryCard = ({ title, value, subtitle, color }) => {
  return (
    <div
      className={`
        ${color}
        text-white
        rounded-xl
        shadow
        p-6
      `}
    >
      <p className="text-sm opacity-90">{title}</p>

      <h2 className="text-2xl xl:text-3xl font-bold mt-2">{value}</h2>

      {subtitle && <p className="text-sm mt-2 opacity-90">{subtitle}</p>}
    </div>
  );
};

export default MonthlyRevenueScreen;
